/**
 * Sticky Buy Button Component
 * Shows a sticky add to cart bar when the main buy buttons are out of view
 */
class StickyBuyButton extends HTMLElement {
  constructor() {
    super();
    
    this.intersectionObserver = null;
    this.mainBuyButtons = null;
    this.mainForm = null;
    this.stickyForm = null;
    this.isVisible = false;
    this.scrollThreshold = 100; // Minimum scroll before showing
  }

  connectedCallback() {
    // Wait a bit for the page to fully load
    setTimeout(() => {
      // Find the main buy buttons block
      this.mainBuyButtons = document.querySelector('.buy-buttons-block');
      if (!this.mainBuyButtons) {
        console.warn('Sticky buy button: Could not find main buy buttons');
        return;
      }

      // Find forms
      this.mainForm = this.mainBuyButtons.querySelector('form[data-type="add-to-cart-form"]');
      this.stickyForm = this.querySelector('form[data-type="add-to-cart-form"]');
      
      if (!this.mainForm || !this.stickyForm) {
        console.warn('Sticky buy button: Could not find required forms');
        return;
      }

      this.init();
    }, 100);
  }

  init() {
    // Set up intersection observer
    this.setupIntersectionObserver();
    
    // Sync form inputs
    this.syncFormInputs();
    
    // Handle form submission
    this.handleFormSubmission();
    
    // Sync variant changes
    this.syncVariantChanges();
  }

  setupIntersectionObserver() {
    const options = {
      rootMargin: `-${this.scrollThreshold}px 0px 0px 0px`,
      threshold: 0
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Show sticky button when main buttons are out of view
        // and user has scrolled past threshold
        const shouldShow = !entry.isIntersecting && window.scrollY > this.scrollThreshold;
        this.toggleVisibility(shouldShow);
      });
    }, options);

    this.intersectionObserver.observe(this.mainBuyButtons);
  }

  toggleVisibility(show) {
    if (show === this.isVisible) return;
    
    this.isVisible = show;
    
    if (show) {
      this.classList.add('is-visible');
      document.body.classList.add('has-sticky-buy-button');
      this.style.display = 'block';
    } else {
      this.classList.remove('is-visible');
      document.body.classList.remove('has-sticky-buy-button');
      
      // Hide after transition
      setTimeout(() => {
        if (!this.isVisible) {
          this.style.display = 'none';
        }
      }, 300);
    }
  }

  syncFormInputs() {
    // Get quantity selector components
    const mainQuantitySelector = this.mainBuyButtons.querySelector('quantity-selector-component');
    const stickyQuantitySelector = this.querySelector('quantity-selector-component');
    
    if (mainQuantitySelector && stickyQuantitySelector) {
      const mainQuantityInput = mainQuantitySelector.querySelector('input[name="quantity"]');
      const stickyQuantityInput = stickyQuantitySelector.querySelector('input[name="quantity"]');
      
      if (mainQuantityInput && stickyQuantityInput) {
        // Initial sync
        stickyQuantityInput.value = mainQuantityInput.value;
        
        // Listen for QuantitySelectorUpdateEvent on the quantity selector components
        mainQuantitySelector.addEventListener('quantity-selector:update', (e) => {
          if (e.detail && e.detail.quantity !== undefined) {
            const newValue = e.detail.quantity.toString();
            if (stickyQuantityInput.value !== newValue) {
              stickyQuantityInput.value = newValue;
            }
          }
        });
        
        stickyQuantitySelector.addEventListener('quantity-selector:update', (e) => {
          if (e.detail && e.detail.quantity !== undefined) {
            const newValue = e.detail.quantity.toString();
            if (mainQuantityInput.value !== newValue) {
              mainQuantityInput.value = newValue;
              // Manually trigger the quantity change on the main selector
              if (mainQuantitySelector.increaseQuantity && mainQuantitySelector.decreaseQuantity) {
                // Use the component's internal method to trigger the event properly
                const currentValue = parseInt(mainQuantityInput.value);
                const targetValue = parseInt(newValue);
                
                // Update the value and trigger the change
                mainQuantityInput.value = newValue;
                // Dispatch the event from the main quantity selector
                mainQuantityInput.dispatchEvent(new CustomEvent('quantity-selector:update', {
                  detail: { quantity: targetValue },
                  bubbles: true
                }));
              }
            }
          }
        });
        
        // Fallback: also listen for regular input events for direct typing
        mainQuantityInput.addEventListener('input', (e) => {
          if (stickyQuantityInput.value !== e.target.value) {
            stickyQuantityInput.value = e.target.value;
          }
        });
        
        stickyQuantityInput.addEventListener('input', (e) => {
          if (mainQuantityInput.value !== e.target.value) {
            mainQuantityInput.value = e.target.value;
          }
        });
      }
    }

    // Sync variant inputs
    const mainVariant = this.mainForm.querySelector('input[name="id"]');
    const stickyVariant = this.stickyForm.querySelector('input[name="id"]');
    
    if (mainVariant && stickyVariant) {
      stickyVariant.value = mainVariant.value;
    }
  }

  handleFormSubmission() {
    // Prevent sticky form from submitting directly
    this.stickyForm.addEventListener('submit', (e) => {
      e.preventDefault();
      
      // Submit the main form instead
      const submitButton = this.mainForm.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.click();
      }
    });
  }

  syncVariantChanges() {
    // Listen for variant changes on the page
    document.addEventListener('variant:change', (event) => {
      const variantId = event.detail.variant?.id;
      if (!variantId) return;
      
      // Update sticky form variant
      const stickyVariant = this.stickyForm.querySelector('input[name="id"]');
      if (stickyVariant) {
        stickyVariant.value = variantId;
      }
      
      // Update button state based on availability
      const stickyButton = this.stickyForm.querySelector('button[type="submit"]');
      const mainButton = this.mainForm.querySelector('button[type="submit"]');
      
      if (stickyButton && mainButton) {
        stickyButton.disabled = mainButton.disabled;
        stickyButton.textContent = mainButton.textContent;
      }
    });
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    document.body.classList.remove('has-sticky-buy-button');
  }
}

// Register custom element
customElements.define('sticky-buy-button', StickyBuyButton);

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickyBuyButton);
} else {
  initStickyBuyButton();
}

function initStickyBuyButton() {
  // Ensure product form component is available
  if (!customElements.get('product-form-component')) {
    // If product form component isn't defined yet, wait a bit
    setTimeout(initStickyBuyButton, 100);
  }
}